<template>
  <b-modal
    :visible="isModalActive"
    :title="modalTitle"
    :ok-title="$t('OK')"
    ok-only
    ok-variant="primary"
    :hide-header-close="isLoading"
    :no-close-on-backdrop="isLoading"
    :no-close-on-esc="isLoading"
    :hide-footer="isLoading"
    size="lg"
    scrollable
    @change="onModalChange"
    @hidden="$emit('close')"
  >
    <div v-if="isLoading" class="text-center py-3">
      <b-spinner variant="primary" class="mb-1" />
      <p class="mb-50 text-muted">{{ $t("Processing import...") }}</p>
      <small class="text-muted">{{ $t("Please wait while the file is being processed.") }}</small>
    </div>

    <div v-else-if="result && result.type === 'success'" class="import-result-body">
      <b-alert show variant="success" class="mb-2">
        {{ result.message }}
      </b-alert>

      <b-row>
        <b-col cols="12" md="6" class="mb-1">
          <strong>{{ $t("Processed Total") }}:</strong> {{ result.processedTotal || 0 }}
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <strong>{{ $t("Created") }}:</strong> {{ result.createdCount || 0 }}
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <strong>{{ $t("Updated") }}:</strong> {{ result.updatedCount || 0 }}
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <strong>{{ $t("Deleted") }}:</strong> {{ result.softDeletedCount || 0 }}
        </b-col>
        <b-col cols="12" md="6" class="mb-1">
          <strong>{{ $t("Skipped") }}:</strong> {{ result.skippedCount || 0 }}
        </b-col>
      </b-row>

      <div v-if="result.errors && result.errors.length" class="mt-2">
        <h6 class="mb-1">{{ $t("Import warnings") }}</h6>
        <b-table
          small
          responsive
          striped
          :items="result.errors"
          :fields="warningFields"
        />
      </div>
    </div>

    <div v-else-if="result && result.type === 'error'" class="import-result-body">
      <b-alert show variant="danger" class="mb-2">
        {{ result.message }}
      </b-alert>

      <div v-if="result.code" class="mb-1">
        <strong>{{ $t("Code") }}:</strong> {{ result.code }}
      </div>
      <div v-if="result.headerRowNumber" class="mb-1">
        <strong>{{ $t("Header Row") }}:</strong> {{ result.headerRowNumber }}
      </div>
      <div v-if="result.sampleRowsChecked" class="mb-1">
        <strong>{{ $t("Sample Rows Checked") }}:</strong> {{ result.sampleRowsChecked }}
      </div>

      <div v-if="detectedHeaderRows.length" class="mt-2">
        <h6 class="mb-1">{{ $t("Detected Headers") }}</h6>
        <b-table
          small
          responsive
          striped
          :items="detectedHeaderRows"
          :fields="headerFields"
        />
      </div>

      <div v-if="result.validationErrors && result.validationErrors.length" class="mt-2">
        <h6 class="mb-1">{{ $t("Validation Errors") }}</h6>
        <b-table
          small
          responsive
          striped
          :items="result.validationErrors"
          :fields="validationFields"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BAlert,
  BRow,
  BCol,
  BTable,
  BSpinner,
} from "bootstrap-vue";

export default {
  name: "MemberImportResultModal",
  components: {
    BModal,
    BAlert,
    BRow,
    BCol,
    BTable,
    BSpinner,
  },
  model: {
    prop: "isModalActive",
    event: "update:is-modal-active",
  },
  props: {
    isModalActive: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    result: {
      type: Object,
      default: null,
    },
  },
  computed: {
    modalTitle() {
      if (this.isLoading) return this.$t("Import Result");
      if (this.result && this.result.type === "success") return this.$t("Import completed");
      if (this.result && this.result.type === "error") return this.$t("Import failed");
      return this.$t("Import Result");
    },
    detectedHeaderRows() {
      if (!this.result || !this.result.detectedHeaders) return [];
      return Object.entries(this.result.detectedHeaders).map(([column, header]) => ({
        column,
        header,
      }));
    },
    headerFields() {
      return [
        { key: "column", label: this.$t("Column") },
        { key: "header", label: this.$t("Header") },
      ];
    },
    validationFields() {
      return [
        { key: "row", label: this.$t("Row") },
        { key: "column", label: this.$t("Column") },
        { key: "message", label: this.$t("Message") },
      ];
    },
    warningFields() {
      return [
        { key: "row", label: this.$t("Row") },
        { key: "username", label: this.$t("Username") },
        { key: "message", label: this.$t("Message") },
      ];
    },
  },
  methods: {
    onModalChange(val) {
      this.$emit("update:is-modal-active", val);
    },
  },
};
</script>

<style scoped>
.import-result-body {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
